import type { SupStoredTxSettings, SupStoredTxSettingsServices } from "@/types";
import React, { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";

import { TxSettingsDictionary } from "@/hooks/tx-flow/settings/useValidateTxSettings";

import useSupported, { type SupportedService } from "@/hooks/api/useSupported";
import { SupBridge, SupDEX } from "@repo/core-ui";
import { SupLoader, SupTagOption, SupTagSelect, SupText } from "@repo/core-ui";

interface SupportedServiceWithFlag extends SupportedService {
  selected: boolean;
}

interface Web3TxSettingsGroupServiceProps {
  type: SupStoredTxSettingsServices;
  settings: SupStoredTxSettings;
  setSettings: Dispatch<SetStateAction<SupStoredTxSettings>>;
}

export default function Web3TxSettingsGroupService({ type, settings, setSettings }: Web3TxSettingsGroupServiceProps) {
  const { data, isLoading } = useSupported();
  const [clicked, setClicked] = useState(false);

  const serviceData = useMemo(() => {
    if (!data) {
      return [];
    }
    return data[type]
      .filter(singleService => !singleService.deposit_disabled)
      .map(
        singleService =>
          ({
            ...singleService,
            selected: !settings.services[type].includes(singleService.key),
          }) as SupportedServiceWithFlag
      );
  }, [data, settings.services, type]);

  const defaultIcon = useMemo(() => {
    return ["bridges", "ambs", "liquidity_providers"].includes(type) ? (
      <SupBridge className="h-6 w-6 bg-info-500 p-0.5" />
    ) : (
      <SupDEX className="h-6 w-6 bg-common-dark-purple p-0.5 text-neutral" />
    );
  }, [type]);

  const onChangeServiceSelection = useCallback(
    (data: Array<string | number>) => {
      if (clicked) {
        const formattedData = data.map(singleData => Number(singleData));
        const filteredServiceKeys = serviceData.filter(serviceKey => !formattedData.includes(serviceKey.key));
        setSettings({
          ...settings,
          services: {
            ...settings.services,
            [type]: filteredServiceKeys.map(singleService => singleService.key),
          },
        });
        setClicked(false);
      }
    },
    [clicked, serviceData, setSettings, settings, type]
  );

  const showErrorMessage = useMemo(() => {
    return serviceData.length - settings.services[type].length < TxSettingsDictionary[type].min;
  }, [serviceData.length, settings.services, type]);

  return (
    <div className="space-y-[26px]" onClick={() => setClicked(true)}>
      <SupText variant="subtitle1">INCLUDED {TxSettingsDictionary[type].title}</SupText>
      {showErrorMessage && (
        <div className="bg-error-50 px-4 py-2">
          <SupText variant="subtitle2" className="text-error-925">
            YOU MUST SELECT AT LEAST {TxSettingsDictionary[type].min} {TxSettingsDictionary[type].title}
          </SupText>
        </div>
      )}
      {isLoading ? (
        <SupLoader />
      ) : (
        serviceData.length > 0 && (
          <SupTagSelect selectAll onChange={onChangeServiceSelection} border small>
            {serviceData.map(singleService => (
              <SupTagOption
                key={`tags-${type}-${singleService.key}`}
                assetIcon={singleService.icon ?? singleService.logo ?? defaultIcon}
                assetLabel={singleService.display_name}
                name={`${singleService.key}`}
                defaultChecked={singleService.selected}
              />
            ))}
          </SupTagSelect>
        )
      )}
    </div>
  );
}
